// The journey map (owner, 2026-10-10): six columns, one chip per client.
//
// When the agent moves a client, her chip slides to the next column (FLIP:
// measured before and after the render, then animated with transform only)
// and glows for two seconds. Each column scrolls on its own; the map never
// grows the page. On a phone: one column at a time, swipe or tap the tabs.
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { useT } from '@/lib/i18n';
import { COLUMNS, COLUMN_LABEL, orderColumn, visibleOf, waitShort, type Chip, type Column, type Tone } from './journey';

const TONE: Record<Tone, string> = {
  red: 'border-status-stuck-border bg-status-stuck-bg text-status-stuck',
  amber: 'border-status-waiting-border bg-status-waiting-bg text-status-waiting',
  blue: 'border-info/45 bg-info/10 text-info',
  plain: 'border-border-subtle bg-bg-elevated text-text-secondary',
};

function ChipButton({ chip, now, selected, onSelect, refFor, big }: {
  chip: Chip; now: number; selected: boolean; onSelect: (c: Chip) => void;
  refFor: (key: string) => (el: HTMLButtonElement | null) => void; big?: boolean;
}) {
  const { t } = useT();
  const flagged = chip.tone === 'red' || chip.tone === 'amber';
  const label = chip.tone === 'red' ? t('bloquée chez nous') : chip.tone === 'amber' ? t('attend ta décision') : chip.tone === 'blue' ? t('l’agent agit') : '';
  return (
    <button
      type="button"
      ref={refFor(chip.key)}
      onClick={() => onSelect(chip)}
      aria-label={`${chip.name}${label ? ` — ${label}` : ''}`}
      className={`w-full flex items-center gap-2 rounded-lg border text-left cursor-pointer transition-colors duration-150 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${big ? 'px-3 py-2.5 min-h-[44px] text-[14px]' : 'px-2.5 py-1.5 text-[12.5px]'} ${TONE[chip.tone]} ${selected ? 'ring-2 ring-brand-500 ring-offset-1 ring-offset-bg-card' : 'hover:brightness-[0.98]'}`}
    >
      {flagged && <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-current shrink-0" />}
      <span className={`truncate min-w-0 flex-1 ${flagged ? 'font-medium' : ''}`}>{chip.name}</span>
      <span className={`shrink-0 tabular-nums text-[11px] ${flagged ? 'font-semibold' : 'text-text-tertiary'}`}>{waitShort(chip.sinceIso, now)}</span>
    </button>
  );
}

function ColumnBody({ chips, now, selectedKey, onSelect, refFor, big, recent }: {
  chips: Chip[]; now: number; selectedKey: string | null; onSelect: (c: Chip) => void;
  refFor: (key: string) => (el: HTMLButtonElement | null) => void; big?: boolean; recent?: number;
}) {
  const { t } = useT();
  const { shown, more } = recent ? visibleOf(chips, recent) : { shown: orderColumn(chips), more: 0 };
  const firstPlain = shown.findIndex((c) => c.tone !== 'red' && c.tone !== 'amber');
  const hasFlagged = firstPlain !== 0 && shown.length > 0;
  if (!shown.length) return <p className="px-3 py-4 text-[12px] text-text-tertiary">{t('Personne ici.')}</p>;
  return (
    <ul className="flex flex-col gap-1.5 p-2">
      {shown.map((c, i) => (
        <li key={c.key}>
          {hasFlagged && i === firstPlain && <p className="px-1 pb-1 pt-0.5 text-[10.5px] text-text-tertiary">{t('bougé récemment')}</p>}
          <ChipButton chip={c} now={now} selected={selectedKey === c.key} onSelect={onSelect} refFor={refFor} big={big} />
        </li>
      ))}
      {more > 0 && <li className="px-2 py-1 text-[12px] text-text-tertiary tabular-nums">+ {more}</li>}
    </ul>
  );
}

export function JourneyMap({ chips, now, selectedKey, onSelect, onArrive, isPhone, header }: {
  chips: Chip[];
  now: number;
  selectedKey: string | null;
  onSelect: (c: Chip) => void;
  /** A client reached « Validé » or « Payé » while you watched (the sound). */
  onArrive?: (column: Column) => void;
  isPhone: boolean;
  header?: ReactNode;
}) {
  const { t } = useT();
  const byCol = new Map<Column, Chip[]>(COLUMNS.map((c) => [c, []]));
  for (const c of chips) byCol.get(c.column)!.push(c);

  // ─── FLIP: the chip that changed column slides from where it was ───────
  const nodes = useRef(new Map<string, HTMLButtonElement>());
  const before = useRef(new Map<string, { rect: DOMRect; col: Column }>());
  const refFor = (key: string) => (el: HTMLButtonElement | null) => {
    if (el) nodes.current.set(key, el); else nodes.current.delete(key);
  };
  const colOf = new Map(chips.map((c) => [c.key, c.column]));
  useLayoutEffect(() => {
    const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    const arrived = new Set<Column>();
    for (const [key, el] of nodes.current) {
      const prev = before.current.get(key);
      const col = colOf.get(key);
      if (!prev || !col || prev.col === col) continue;
      if (col === 'valide' || col === 'paye') arrived.add(col);
      el.classList.add('jm-lit');
      window.setTimeout(() => el.classList.remove('jm-lit'), 2300);
      if (reduce || typeof el.animate !== 'function') continue;
      const now = el.getBoundingClientRect();
      const dx = prev.rect.left - now.left;
      const dy = prev.rect.top - now.top;
      if (Math.abs(dx) + Math.abs(dy) < 2) continue;
      el.animate([{ transform: `translate(${dx}px, ${dy}px)` }, { transform: 'translate(0, 0)' }],
        { duration: 650, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' });
    }
    const next = new Map<string, { rect: DOMRect; col: Column }>();
    for (const [key, el] of nodes.current) {
      const col = colOf.get(key);
      if (col) next.set(key, { rect: el.getBoundingClientRect(), col });
    }
    before.current = next;
    for (const col of arrived) onArrive?.(col);
  });

  // ─── PHONE: one column, swipe between them ─────────────────────────────
  const [phoneCol, setPhoneCol] = useState<Column>('photos');
  const touch = useRef<{ x: number; y: number; axis: 'x' | 'y' | null } | null>(null);
  const step = (d: number) => {
    const i = COLUMNS.indexOf(phoneCol);
    setPhoneCol(COLUMNS[Math.min(COLUMNS.length - 1, Math.max(0, i + d))]);
  };

  if (isPhone) {
    return (
      <section className="rounded-xl border border-border-subtle bg-bg-card overflow-hidden">
        {header}
        <div role="tablist" aria-label={t('Étapes')} className="flex gap-1.5 overflow-x-auto px-3 py-2.5 border-b border-border-subtle">
          {COLUMNS.map((c) => {
            const list = byCol.get(c)!;
            const alarm = list.some((x) => x.tone === 'red');
            return (
              <button key={c} type="button" role="tab" aria-selected={phoneCol === c} onClick={() => setPhoneCol(c)}
                className={`shrink-0 inline-flex items-center gap-1.5 rounded-full border px-3 min-h-[36px] text-[12.5px] cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 ${phoneCol === c ? 'border-brand-500 bg-brand-500/15 text-brand-700 dark:text-brand-300 font-semibold' : 'border-border-subtle bg-bg-elevated text-text-secondary'}`}>
                {t(COLUMN_LABEL[c])}
                <span className={`tabular-nums text-[11px] ${alarm ? 'text-status-stuck font-semibold' : 'text-text-tertiary'}`}>{list.length}</span>
              </button>
            );
          })}
        </div>
        <div
          className="h-[48dvh] overflow-y-auto"
          style={{ touchAction: 'pan-y' }}
          onTouchStart={(e) => { const p = e.touches[0]; touch.current = { x: p.clientX, y: p.clientY, axis: null }; }}
          onTouchMove={(e) => {
            const s = touch.current; if (!s || s.axis) return;
            const p = e.touches[0]; const dx = p.clientX - s.x; const dy = p.clientY - s.y;
            if (Math.abs(dx) > 12 || Math.abs(dy) > 12) s.axis = Math.abs(dx) > Math.abs(dy) * 1.3 ? 'x' : 'y';
          }}
          onTouchEnd={(e) => {
            const s = touch.current; touch.current = null;
            if (!s || s.axis !== 'x' || s.x < 24) return;
            const dx = e.changedTouches[0].clientX - s.x;
            if (Math.abs(dx) > 60) step(dx < 0 ? 1 : -1);
          }}
        >
          <ColumnBody chips={byCol.get(phoneCol)!} now={now} selectedKey={selectedKey} onSelect={onSelect} refFor={refFor} big recent={phoneCol === 'ecrit' ? 10 : undefined} />
        </div>
        <div className="flex justify-center gap-1.5 py-2" aria-hidden>
          {COLUMNS.map((c) => <span key={c} className={`h-1.5 w-1.5 rounded-full ${c === phoneCol ? 'bg-brand-500' : 'bg-border-subtle'}`} />)}
        </div>
      </section>
    );
  }

  return (
    <section>
      {header}
      <div className="grid grid-cols-6 gap-2.5 h-[440px]">
        {COLUMNS.map((c, i) => {
          const list = byCol.get(c)!;
          const red = list.filter((x) => x.tone === 'red').length;
          return (
            <div key={c} className="flex flex-col min-h-0 rounded-xl border border-border-subtle bg-bg-card">
              <div className="flex items-center gap-2 px-3 py-2.5 border-b border-border-subtle">
                <span aria-hidden className="h-5 w-5 shrink-0 rounded-full border border-brand-500 text-[10.5px] font-semibold text-brand-700 dark:text-brand-300 grid place-items-center">{i + 1}</span>
                <b className="flex-1 text-[13px] font-semibold text-text-primary">{t(COLUMN_LABEL[c])}</b>
                {red > 0 && <span className="text-[11px] font-semibold text-status-stuck tabular-nums" title={t('bloquées chez nous')}>{red}</span>}
                <span className="text-[11.5px] text-text-tertiary tabular-nums">{list.length}</span>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto">
                <ColumnBody chips={list} now={now} selectedKey={selectedKey} onSelect={onSelect} refFor={refFor} recent={c === 'ecrit' ? 10 : undefined} />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
